import { useSuspenseQuery } from '@tanstack/react-query';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import { Badge, BadgeIconWrapper, IntlPluralize, ListItem } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Dialog,
    DialogActionButton,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    Heading,
} from '@gitbook/spine-react';
import { assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import { BacklinksAlert, BacklinksAlertWrapper, useContentNavigation } from '../../../Components';
import { apiQuery, useSupportsBacklinks } from '../../../state';
import { useSpaceContext } from '../context';

interface DeletePageDialogProps {
    isOpen: boolean;
    onClose: () => void;
    page: hiveCore.RevisionPage;
    onConfirm?: () => void;
}

/**
 * Dialog to confirm the deletion of a page.
 */
export function DeletePageDialog(props: DeletePageDialogProps) {
    const { isOpen, onClose } = props;
    return (
        <DialogRoot
            isOpen={isOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onClose();
                }
            }}
        >
            <Dialog>
                <DeletePageDialogContent {...props} />
            </Dialog>
        </DialogRoot>
    );
}

/**
 * Modal to confirm the deletion of page.
 */
function DeletePageDialogContent(props: DeletePageDialogProps) {
    const { page, onClose, onConfirm } = props;
    const hiveBranch = useHiveBranch();
    const { revision } = useHiveRevision();
    const { space, changeRequest, analyticsContext } = useSpaceContext();

    const supportsBacklinks = useSupportsBacklinks({ isInChangeRequest: !!changeRequest });

    const onComplete = async () => {
        assertIsDefined(hiveBranch);

        await hiveBranch.change((change) => {
            change.removePage(page.id, {
                deleteEmptyGroupParent: true,
            });
        });

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_remove_page',
            pageKind: page.type,
        });

        onConfirm?.();
        onClose();
    };

    return (
        <>
            <DialogHeader>
                <Heading>
                    {page.type === hiveCore.RevisionPageType.Group ? 'Delete group' : 'Delete page'}
                </Heading>
            </DialogHeader>
            <DialogBody>
                <div className="flex flex-col gap-4">
                    <div>
                        Confirming will delete all the content; however, it will still be available
                        in the version history.
                    </div>
                    {supportsBacklinks && page.type === hiveCore.RevisionPageType.Document ? (
                        <BacklinksAlertWrapper contentType="page">
                            <WarnOfPageBacklinks
                                space={space}
                                changeRequest={changeRequest}
                                page={page}
                                onClose={onClose}
                            />
                        </BacklinksAlertWrapper>
                    ) : null}
                </div>
                {page.type === hiveCore.RevisionPageType.Group ||
                page.type === hiveCore.RevisionPageType.Document ? (
                    <ConflictChildPages revision={revision} page={page} onClose={onClose} />
                ) : null}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <DialogActionButton
                    kind="danger"
                    label="Delete"
                    onPress={() => {
                        return onComplete();
                    }}
                />
            </DialogFooter>
        </>
    );
}

/** Warn the users about links to this page. */
function WarnOfPageBacklinks(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    page: hiveCore.RevisionPage;
    onClose: () => void;
}): React.ReactElement | null {
    const { space, changeRequest, page, onClose } = props;

    const pageBacklinkResults = useSuspenseQuery(
        changeRequest
            ? apiQuery(
                  'listChangeRequestPageBacklinks',
                  {
                      spaceId: space.id,
                      changeRequestId: changeRequest.id,
                      pageId: page.id,
                      limit: 20,
                  },
                  { nullOnError: true }
              )
            : apiQuery(
                  'listSpacePageBacklinks',
                  {
                      spaceId: space.id,
                      pageId: page.id,
                      limit: 20,
                  },
                  { nullOnError: true }
              )
    );

    const contentLocations = pageBacklinkResults?.data?.items ?? [];
    if (contentLocations.length === 0) {
        return null;
    }

    return (
        <BacklinksAlert
            contentType="page"
            contentLocations={contentLocations}
            onNavigate={onClose}
        />
    );
}

function ConflictChildPages(props: {
    page: hiveCore.RevisionPage;
    revision: hiveCore.Revision;
    onClose: () => void;
}) {
    const { page, revision, onClose } = props;
    const { navigateToPage } = useContentNavigation();

    const pages = hiveCore.getPageDescendantsInRevision(revision, page.id);

    const deletedInBase = pages
        .map((page) =>
            revision.conflicts?.pages[page.id] === hiveCore.RevisionConflictState.DeletedInBase
                ? page
                : null
        )
        .filter(filterOutNullable);

    const count = deletedInBase.length;

    return count ? (
        <div className="mt-4 flex flex-col">
            <div className="mb-4 flex">
                <Badge kind="warning" icon={Icon.Warning} />
                <div className="ml-4 text-ui-standout">
                    There {count > 1 ? 'are' : 'is'} {deletedInBase.length} nested{' '}
                    <IntlPluralize
                        count={Object.keys(deletedInBase.length).length}
                        singular="page"
                        plural="pages"
                    />{' '}
                    deleted in the primary content but modified in your change request.
                    <br />
                    Deleting the current page, you would delete {count > 1 ? 'all of them' : 'it'}.
                </div>
            </div>
            <div className="flex">
                {deletedInBase.map((page) => {
                    return (
                        <ListItem
                            key={page.id}
                            layout="wide"
                            icon={
                                <BadgeIconWrapper>
                                    <Badge kind="danger" icon={Icon.Trashcan} />
                                </BadgeIconWrapper>
                            }
                            title={page.title}
                            buttons={[
                                <Button
                                    key="open"
                                    size="medium"
                                    kind="secondary"
                                    onPress={() => {
                                        onClose();
                                        navigateToPage(revision, page.id);
                                    }}
                                    label="Open"
                                />,
                            ]}
                        />
                    );
                })}
            </div>
        </div>
    ) : null;
}
